import { Component } from '@angular/core';
import permissions from 'app/models/Permissions';
import partner from 'app/models/partner';
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { PartnerService } from '../../../services/partner.service';
import { PartnerUserService } from '../../../services/partner-user.service';
import { StoreService } from '../../../services/store.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import Utils from '../../../shared/utils';
import { buildLaunchpadCommandUrl } from '../../../shared/launchpad-command';

@Component({
  selector: 'app-components-manager-managepartners',
  templateUrl: './ManagePartners.component.html'
})
export class ManagePartnersComponent implements IActivatable {
  isLoading: boolean;
  partnerNameFilter = '';
  partners: IPartnerMinimalWithHasPartnerUser[] = [];
  filteredPartners: IPartnerMinimalWithHasPartnerUser[] = [];
  partnerUsers: Boo.Objects.PartnerUser[] = [];
  user: Boo.Objects.User;
  canManageStorefronts: boolean;
  boostabilityPartnerId: Boo.Objects.Enums.PartnerEnum = Boo.Objects.Enums.PartnerEnum.Boostability;
  activeOnly = true;
  showLivePartners = true;
  private partnerTemplate: Boo.Objects.Partner = new partner() as Boo.Objects.Partner;

  constructor(
    private partnerService: PartnerService,
    private partnerUserService: PartnerUserService,
    private storeService: StoreService,
    private sessionStorageService: SessionStorageService) { }

  canActivate(user: Boo.Objects.User, partner: Boo.Objects.Partner): boolean {
    this.canManageStorefronts = Utils.hasPermission(partner, user.PartnerUsers, permissions.CanManageStorefronts, user) ||
      Utils.hasPermission(partner, user.PartnerUsers, permissions.CanManageStorefrontsLimited, user) ||
      Utils.hasPermission(partner, user.PartnerUsers, permissions.CanOnboardStorefronts, user);
    return true;
  }

  activate(params: any): JQueryPromise<any> {
    this.isLoading = true;
    return Utils.wrapDfd(this.sessionStorageService.getUser())
      .then((user: Boo.Objects.User) => {
        this.user = user;
        return Utils.wrapDfd(this.partnerUserService.getByUserId(user.UserId))
          .then((partnerUsers: Boo.Objects.PartnerUser[]) => {
            this.partnerUsers = partnerUsers;
          }).then(this.loadPartners.bind(this));

      }).fail((displayMessage: string) => {
        toastr.error(displayMessage);
      })
      .always(() => {
        this.isLoading = false;
      });
  }

  setActiveOnly(val: boolean): void {
    this.activeOnly = val;
    this.filterPartners();
  }

  resetNameFilter(): void {
    this.partnerNameFilter = '';
    this.filterPartners();
  }

  setShowLive(val: boolean): void {
    this.showLivePartners = val;
    this.filterPartners();
  }

  filterPartners(): void {
    const searchTerm = this.partnerNameFilter.trim().toLowerCase();

    this.filteredPartners = this.partners.filter(p => {
      const matchesName = p.PartnerName.trim().toLowerCase().includes(searchTerm);
      const matchesActive = this.activeOnly ? p.IsPartnerActive : true;
      const matchesType  = this.showLivePartners ? p.IsPartnerLive : !p.IsPartnerLive;
      return matchesName && matchesActive && matchesType;
    });
  }

  addPartner(): void {
    const activationOptions = {
      selectedPartner: this.partnerTemplate
    };

    PriorityViewModel.show('app-components-manager-partner', activationOptions).always(this.loadPartners.bind(this));
  }

  editPartner(partnerMin: Boo.Objects.PartnerMinimal): void {
    $.when(Utils.wrapDfd(this.partnerService.get(partnerMin.PartnerId)))
      .then((partner: Boo.Objects.Partner): void => {
        const activationOptions = {
          selectedPartner: partner
        };

        PriorityViewModel.show('app-components-manager-partner', activationOptions).always(this.loadPartners.bind(this));
      });
  }

  manageStorefront(partnerMin: Boo.Objects.PartnerMinimal): void {
    Utils.wrapDfd(this.storeService.ssoStorefronts('/storefront/all/' + partnerMin.StorefrontId))
      .then(url => window.location.href = url)
      .fail(err => toastr.error(err));
  }

  createStorefront(partnerMin: Boo.Objects.PartnerMinimal): void {
    Utils.wrapDfd(this.storeService.ssoStorefronts('/storefront/all?partnerid=' + partnerMin.PartnerId + '&name=' + partnerMin.PartnerName))
      .then(url => window.location.href = url)
      .fail(err => toastr.error(err));
  }

  openReporting(partnerMin: Boo.Objects.PartnerMinimal): void {
    PriorityViewModel.show('app-components-manager-managereporting', {
      partner: partnerMin
    }).fail((errorMessage: string) => {
      toastr.error(`There was a problem loading reporting site. ${errorMessage}`);
    });
  }

  openTaskTypeExclusions(partnerMin: Boo.Objects.PartnerMinimal): void {
    PriorityViewModel.show('app-components-manager-partner-tasktype-exclusions', {
      partner: partnerMin
    }).fail((err: string) => {
      toastr.error(`There was a problem loading partner task type exclusions. ${err}`);
    });
  }

  openPartnerWorkConfigurations(partnerMin: Boo.Objects.PartnerMinimal, isReadOnly: boolean): void {
    PriorityViewModel.ngShow('app-components-manager-partner-work-configurations', { partner: partnerMin, isReadOnly: isReadOnly });
  }

  openPartnerEmails(partnerMin: Boo.Objects.PartnerMinimal): void {
    PriorityViewModel.ngShow('app-components-manager-partner-emails', { partnerMinimal: partnerMin });
  }

  addUserToPortal(partner: IPartnerMinimalWithHasPartnerUser): void {
    this.confirmAddUserToPortal()
      .then(() => {
        Utils.wrapDfd(this.partnerUserService.addCurrentUserToPartner(partner.PartnerId))
          .then(() => {
            toastr.success('User successfully added');
            partner.hasPartnerUserInPartner = true;
          })
          .fail((displayMessage: string) => {
            toastr.error(displayMessage);
          });
      });
  }

  openLaunchpadCommand(partner: Boo.Objects.PartnerMinimal): void {
    window.open(buildLaunchpadCommandUrl(partner.PartnerId), '_blank');
  }

  managePartnerPricingTool(partnerRow: Boo.Objects.Partner): void {
    this.isLoading = true;

    Utils.wrapDfd(this.partnerService.get(partnerRow.PartnerId))
      .then((partner) => {
        PriorityViewModel.show('app-components-manager-managepartnerpricingtool', { partner: partner }).always(this.loadPartners.bind(this));
      })
      .fail((message: string) => {
        toastr.error(message);
      })
      .always(() => {
        this.isLoading = false;
      });
  }

  deactivatePartner(partner: Boo.Objects.PartnerMinimal): void {
    bootbox.dialog({
      message: `Are you sure you want to deactivate <strong>${partner.PartnerName}</strong>?`,
      closeButton: false,
      buttons: {
        no: {
          label: 'Cancel'
        },
        yes: {
          label: 'Yes',
          className: 'btn-danger',
          callback: (): void => {
            this.isLoading = true;
            Utils.wrapDfd(this.partnerService.deactivate(partner.PartnerId, this.user.UserId))
              .then(() => {
                toastr.success(`${partner.PartnerName} successfully deactivated`);
                return this.loadPartners();
              })
              .fail((displayMessage: string) => {
                toastr.error(displayMessage);
              })
              .always(() => this.isLoading = false);
          }
        }
      }
    });
  }

  reactivatePartner(partner: Boo.Objects.PartnerMinimal): void {
    bootbox.dialog({
      message: `Are you sure you want to reactivate <strong>${partner.PartnerName}</strong>?`,
      closeButton: false,
      buttons: {
        no: {
          label: 'Cancel'
        },
        yes: {
          label: 'Yes',
          className: 'btn-success',
          callback: (): void => {
            this.isLoading = true;
            Utils.wrapDfd(this.partnerService.reactivate(partner.PartnerId))
              .then(() => {
                toastr.success(`${partner.PartnerName} successfully reactivated`);
                return this.loadPartners();
              })
              .fail((displayMessage: string) => {
                toastr.error(displayMessage);
              })
              .always(() => this.isLoading = false);
          }
        }
      }
    });
  }

  closeBillingIssueTickets(partner: Boo.Objects.PartnerMinimal): void {
    bootbox.dialog({
      message: `Are you sure you want to close all open billing issue tickets for all customers of <strong>${partner.PartnerName}</strong>?`,
      closeButton: false,
      buttons: {
        no: {
          label: 'Cancel'
        },
        yes: {
          label: 'Yes',
          className: 'btn-success',
          callback: (): void => {
            this.isLoading = true;
            Utils.wrapDfd(this.partnerService.closeAllOpenBillingIssueTickets(partner.PartnerId))
              .then(() => {
                toastr.success(`All open billing issue tickets closed for ${partner.PartnerName}`);
                return this.loadPartners();
              })
              .fail((displayMessage: string) => {
                toastr.error(displayMessage);
              })
              .always(() => this.isLoading = false);
          }
        }
      }
    });
  }

  private loadPartners(): JQueryPromise<void> {
    return Utils.wrapDfd(this.partnerService.getPartnerMinimals())
      .then((partners) => {
        _.each(partners, (partner: IPartnerMinimalWithHasPartnerUser) => {
          partner.hasPartnerUserInPartner = _.any(this.partnerUsers, (partnerUser: Boo.Objects.PartnerUser) => {
            return partnerUser.PartnerId === partner.PartnerId;
          });
        });
        this.partners = partners as IPartnerMinimalWithHasPartnerUser[];
        this.filterPartners();
      });
  }

  private confirmAddUserToPortal(): JQueryPromise<boolean> {
    return $.Deferred<boolean>((dfd) => {
      const msg = 'Are you sure you would like to add your user to this portal?';
      bootbox.confirm(msg, (result: boolean) => {
        if (result === true) {
          dfd.resolve();
        } else {
          dfd.reject();
        }
      });
    }).promise();
  }
}

interface IPartnerMinimalWithHasPartnerUser extends Boo.Objects.PartnerMinimal {
  hasPartnerUserInPartner: boolean;
}
